import React, { useEffect, useState } from "react";
import { colors } from "@styles/colors";
import { Text, YStack, XStack, Button, Sheet, ScrollView, Spinner } from "tamagui";
import { RiCoinsLine, RiCloseFill } from "react-icons/ri";
import { handleAPIListPaymentMethods, OwnedPaymentMethod } from "@screen/Home/pages/Account/API/handlePaymentAPI";
import KioskAddCard from "@screen/Kiosk/AddCard";
import { AddCardRow, CardBrand, LeadingBadge, PaymentOptionRow } from "@components/payment/PaymentOptionRow";
import { RiWallet3Line } from "react-icons/ri";
import { IPASS_PAY_REF, startIPassAuthorization } from "@utils/ipassMoney";
import { TapPaySecurityNote } from "@components/payment/TapPayCompliance";

export function PayMethodSwitchMenu({
  themeColor = "white",
  changeColor,
  flush = false,
  monthly = false,
  value,
  onChange,
  cusJwtToken,
  elecEnabled = false,
  ipassOrderId,
  ipassBranchName,
  onIPassSelected,
}: {
  themeColor?: string;
  changeColor?: string;
  /** 觸發鈕不帶水平內距，讓值與上方區塊標題切齊（瓦斯訂購 Skin V2 用）。 */
  flush?: boolean;
  /** 月結客戶（cis.customer_type === 月結客戶）→ 付款方式顯示「月結」而非「貨到付款」 */
  monthly?: boolean;
  /** 目前選定的 payment_method_ref（cash_on_delivery / 已綁卡 id）。電子付款開啟時生效。 */
  value?: string;
  /** 選定付款方式時回寫（電子付款開啟時提供）。 */
  onChange?: (ref: string, label: string) => void;
  /** 撈已綁卡用；電子付款開啟時提供。 */
  cusJwtToken?: string;
  /** 電子付款開關（supplier platform_setting.enable_electronic_payment，由父層帶入）。 */
  elecEnabled?: boolean;
  /** iPASS 授權要帶的訂單編號（草稿亦可，授權完成後導回會帶它） */
  ipassOrderId?: string;
  /** 顯示在一卡通授權頁上的瓦斯行名稱 */
  ipassBranchName?: string;
  /**
   * 選了 iPASS Money 時由父層接手（存草稿 → 判斷是否已授權 → 導去授權）。
   * 有給就不走元件內建的直接導轉。
   */
  onIPassSelected?: () => Promise<void> | void;
}) {
  const activeColor = colors[themeColor as keyof typeof colors] || themeColor;
  const changeTextColor = changeColor ? colors[changeColor as keyof typeof colors] || changeColor : activeColor;
  const [open, setOpen] = useState(false);
  const [cards, setCards] = useState<OwnedPaymentMethod[]>([]);
  const [loading, setLoading] = useState(false);
  // 新增信用卡以 overlay 疊上（不換路由）→ ✕ 收起即回到購物車。
  const [showAddCard, setShowAddCard] = useState(false);
  // 綁卡成功後遞增 → 重新載入卡片清單。
  const [reload, setReload] = useState(0);
  const elecOn = elecEnabled;

  // 電子付款開啟才撈已綁卡（關閉時完全維持原本「貨到/月結」單一顯示行為）。
  useEffect(() => {
    if (!elecOn || !cusJwtToken) return;
    let alive = true;
    setLoading(true);
    handleAPIListPaymentMethods(cusJwtToken)
      .then((r) => {
        if (!alive) return;
        setCards(r.state === "success" ? r.items.filter((c) => c.method_type === "credit_card") : []);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [elecOn, cusJwtToken, reload]);

  const cashLabel = monthly ? "月結" : "貨到付款";
  const cardLabel = (c: OwnedPaymentMethod) => `信用卡 •••• ${c.card?.lastfour ?? "----"}`;
  const selectedCard = elecOn ? cards.find((c) => c.payment_method_id === value) : undefined;
  const isIPass = elecOn && value === IPASS_PAY_REF;
  const currentPayMethod = selectedCard ? cardLabel(selectedCard) : isIPass ? "iPASS Money" : cashLabel;
  const resPayMethodList = [currentPayMethod];

  const pick = (ref: string, label: string) => {
    onChange?.(ref, label);
    setOpen(false);
  };

  const optionClass = (isSelected: boolean) =>
    `flex items-center gap-3 px-3 py-3 mt-3 rounded-xl border w-full text-left transition-colors ${
      isSelected ? "border-blue-200 bg-blue-50 active:bg-blue-100" : "border-gray-200 bg-gray-50 active:bg-gray-100"
    }`;

  return (
    <>
      <Button
        backgroundColor="rgba(255, 255, 255, 0.2)"
        p={10}
        px={flush ? 0 : 10}
        py={flush ? 0 : 10}
        // flush（結帳頁卡片內）高度與 AddressSwitchMenu／配送時間列一致，
        // 三張卡的值列才會落在同一個行盒高度上，卡內間距看起來才均勻。
        h={flush ? 21 : 40}
        width="100%"
        hoverStyle={{ backgroundColor: "rgba(255, 255, 255, 0.2)" }}
        pressStyle={{ backgroundColor: "rgba(255, 255, 255, 0.2)", opacity: 0.8 }}
        borderColor="rgba(255,255,255,0.2)"
        borderWidth="$0.2"
        onPress={() => setOpen(true)}>
        <XStack justifyContent="space-between" ai="center" width="100%">
          <XStack ai="center" gap={8} flex={1}>
            {loading ? (
              <Spinner size="small" color={activeColor} />
            ) : currentPayMethod ? (
              <Text col={activeColor} fontSize={14} fontWeight="bold" numberOfLines={1} flex={1} textAlign="left">
                {currentPayMethod}
              </Text>
            ) : (
              <Text col={activeColor} fontSize={14} fontWeight="bold" numberOfLines={1} flex={1} textAlign="left">
                尚未設定支付方式
              </Text>
            )}
          </XStack>
          <Text color={changeTextColor} fontSize={14} fontWeight="bold" ml="$2">
            變更
          </Text>
        </XStack>
      </Button>
      <Sheet
        disableDrag
        modal
        open={open}
        onOpenChange={setOpen}
        snapPoints={[95]}
        dismissOnSnapToBottom={false}
        dismissOnOverlayPress={false}
        animation="medium">
        <Sheet.Overlay animation="lazy" enterStyle={{ opacity: 0 }} exitStyle={{ opacity: 0 }} backgroundColor="rgba(0,0,0,0.4)" />
        <Sheet.Handle />
        <Sheet.Frame bg="#ffffff" borderTopLeftRadius={24} borderTopRightRadius={24}>
          <XStack alignItems="center" justifyContent="space-between" padding="$5">
            <YStack>
              <Text fontWeight="bold">選擇支付方式</Text>
            </YStack>
            <Button
              p={4}
              chromeless
              onPress={() => setOpen(false)}
              backgroundColor={colors.primaryActive}
              pressStyle={{ backgroundColor: colors.primaryActive, borderColor: colors.primaryActive, borderWidth: 1 }}
              hoverStyle={{ backgroundColor: colors.primaryActive, borderColor: colors.primaryActive, borderWidth: 1 }}>
              <RiCloseFill size={22} />
            </Button>
          </XStack>
          <ScrollView showsVerticalScrollIndicator={false} background={colors.bg}>
            <YStack gap={3} p="$3">
              {elecOn ? (
                <YStack gap={10} pt="$2">
                  {/* 已綁信用卡（左側卡別 logo，切版同練習模式 PaymentOptions） */}
                  {loading ? (
                    <YStack ai="center" py={16}>
                      <Spinner size="small" color={colors.primary} />
                    </YStack>
                  ) : (
                    cards.map((c) => (
                      <PaymentOptionRow
                        key={c.payment_method_id}
                        title={cardLabel(c)}
                        sub={c.card?.issuer || c.nickname || "信用卡"}
                        leading={<CardBrand bincode={c.card?.bincode} />}
                        selected={value === c.payment_method_id}
                        onPress={() => pick(c.payment_method_id, cardLabel(c))}
                      />
                    ))
                  )}
                  {/* 貨到付款 / 月結（沒選卡即視同此項） */}
                  <PaymentOptionRow
                    title={cashLabel}
                    sub={monthly ? "月底結算" : "送達時付款"}
                    leading={
                      <LeadingBadge bg="#eafaf0">
                        <RiCoinsLine size={20} color={colors.green} />
                      </LeadingBadge>
                    }
                    // 「沒選卡」不等於「貨到付款」——iPASS 也不是卡，要另外排除
                    selected={!selectedCard && !isIPass}
                    onPress={() => pick("cash_on_delivery", cashLabel)}
                  />
                  {/*
                    * iPASS Money 自動扣款。
                    *
                    * 跟信用卡／貨到付款不一樣：選了之後**當下就要導去一卡通授權頁**，
                    * 授權完成才拿得到 regKey，配送員送達簽收後才扣得了款。
                    * 所以這一列不是單純改個值，而是會離開這個頁面。
                    */}
                  <PaymentOptionRow
                    title="iPASS Money"
                    sub="送達簽收後自動扣款，免掃碼"
                    leading={
                      <LeadingBadge bg="#fff4e6">
                        <RiWallet3Line size={20} color="#f08c00" />
                      </LeadingBadge>
                    }
                    selected={value === IPASS_PAY_REF}
                    onPress={() => {
                      setOpen(false);
                      onChange?.(IPASS_PAY_REF, "iPASS Money");
                      // 授權是離站流程：父層要先把選擇存進草稿再導走，
                      // 否則導回來時表單會被草稿的舊值蓋回貨到付款，變成無限迴圈。
                      if (onIPassSelected) {
                        void onIPassSelected();
                        return;
                      }
                      // 沒有客戶 JWT 就無法授權（後端以 JWT 歸戶），直接退回貨到付款
                      if (!cusJwtToken) {
                        onChange?.("cash_on_delivery", cashLabel);
                        return;
                      }
                      void startIPassAuthorization({
                        orderId: ipassOrderId || `DRAFT_${Date.now()}`,
                        amount: 0,
                        branchName: ipassBranchName,
                        jwtToken: cusJwtToken,
                      }).catch(() => {
                        onChange?.("cash_on_delivery", cashLabel);
                      });
                    }}
                  />
                  {/* 新增信用卡（放最後） */}
                  <AddCardRow
                    onPress={() => {
                      setOpen(false);
                      setShowAddCard(true);
                    }}
                  />
                  {/* TapPay 金流安全揭露（審核要求：電子支付選項處需說明金流商與加密／PCI 認證） */}
                  <TapPaySecurityNote compact />
                </YStack>
              ) : (
                resPayMethodList.map((item) => {
                  const isSelected = currentPayMethod === item;
                  return (
                    <button
                      key={item}
                      type="button"
                      onClick={() => {
                        setOpen(false);
                      }}
                      className={optionClass(isSelected)}>
                      <RiCoinsLine className={`w-5 h-5 flex-shrink-0 ${isSelected ? "text-blue-500" : "text-gray-400"}`} />
                      <div>
                        <div className={`font-semibold text-sm leading-snug ${isSelected ? "text-gray-800" : "text-gray-500"}`}>{item}</div>
                      </div>
                    </button>
                  );
                })
              )}
            </YStack>
          </ScrollView>
        </Sheet.Frame>
      </Sheet>
      {showAddCard && <KioskAddCard onClose={() => setShowAddCard(false)} onBound={() => setReload((k) => k + 1)} cusJwtToken={cusJwtToken} />}
    </>
  );
}
